import {
  DealForfeitResultSchema,
  DealQueueResponseSchema,
  type DealForfeitResult,
  type DealQueueResponse,
  type DealQueueSort,
  type DealQueueTab,
} from '@carsfinders/contracts';

import { ApiError, apiFetch } from '../../lib/api';

/**
 * The «Угоди» screen's data layer — one function per endpoint, nothing else.
 *
 *   GET  /api/admin/deals/queue?tab=&q=&cursor=      -> DealQueueResponse (phase 7)
 *   POST /api/admin/deals/:dealId/advance-shipping   -> {fromShipStep}    (phase 7)
 *   POST /api/admin/deals/:dealId/hand-over          -> «Видано клієнту»  (phase 7)
 *   POST /api/admin/finance/invoices/:no/mark-paid   -> «Оплату отримано» (phase 6)
 *   POST /api/admin/finance/invoices/:no/remind      -> «Нагадати»        (phase 6)
 *   POST /api/admin/deals/:dealId/forfeit-deposit    -> «Утримати депозит» (decision a)
 *
 * Mirroring and the outcome go through the relay desk's own routes
 * (`features/bids/api.ts#relayAction`), and the invoice through the finance
 * drawer — those are the machines that own those writes, so this file does
 * not grow a second client for them.
 *
 * The queue is PARSED, not cast, for the same reason as `finance/api.ts`: rows
 * carry `z.coerce.date()` fields (`stageEnteredAt`, `invoice.dueAt`) that
 * arrive as strings.
 */

/** Prefix of every queue query, so a bid event can invalidate all tabs and searches at once. */
export const DEALS_QUEUE_QUERY_KEY = ['admin', 'deals', 'queue'] as const;

export const dealsQueueQueryKey = (
  tab: DealQueueTab,
  q: string,
  sort?: DealQueueSort,
  userId?: string,
  auction?: string,
) => [...DEALS_QUEUE_QUERY_KEY, tab, q, sort ?? 'queue', userId ?? '', auction ?? ''] as const;

export interface DealsQueueParams {
  tab: DealQueueTab;
  /** Omitted = the server's work order (`queue`). */
  sort?: DealQueueSort;
  /** One client's lots only — the client page's «Ставки» tab. */
  userId?: string;
  /** One auction house's lots only — «Угоди»'s «Аукціон» filter. */
  auction?: string;
  q?: string;
  cursor?: string;
  /** Page size; the server defaults to 50 and caps at 100. */
  limit?: number;
}

/** One page of the queue plus the per-tab counters (counters honour `q`, ignore `tab`). */
export async function fetchDealsQueue({
  tab,
  sort,
  userId,
  auction,
  q,
  cursor,
  limit,
}: DealsQueueParams): Promise<DealQueueResponse> {
  const query = q?.trim();
  const payload = await apiFetch<unknown>('admin/deals/queue', {
    searchParams: {
      tab,
      sort,
      userId,
      auction,
      q: query ? query : undefined,
      cursor,
      limit: limit === undefined ? undefined : String(limit),
    },
  });
  return DealQueueResponseSchema.parse(payload);
}

/**
 * «Наступний етап»: Доставка k → k+1. `fromShipStep` is the step the operator
 * SAW, so a double click answers 409 `STALE_SHIP_STEP` instead of advancing twice.
 */
export function advanceShipping(dealId: string, fromShipStep: number): Promise<unknown> {
  return apiFetch(`admin/deals/${dealId}/advance-shipping`, {
    method: 'POST',
    body: { fromShipStep },
  });
}

/** «Видано клієнту»: `delivered → closed`. */
export function handOver(dealId: string): Promise<unknown> {
  return apiFetch(`admin/deals/${dealId}/hand-over`, { method: 'POST', body: {} });
}

/** «Оплату отримано» — with no amount the API settles everything still owed. */
export function markInvoicePaid(invoiceNo: string): Promise<unknown> {
  return apiFetch(`admin/finance/invoices/${encodeURIComponent(invoiceNo)}/mark-paid`, {
    method: 'POST',
    body: {},
  });
}

/** «Нагадати» — queues a payment reminder (throttled server-side to one per 24 h). */
export function remindInvoice(invoiceNo: string): Promise<unknown> {
  return apiFetch(`admin/finance/invoices/${encodeURIComponent(invoiceNo)}/remind`, {
    method: 'POST',
    body: {},
  });
}

/**
 * «Утримати депозит (відмова від оплати)» — owner decision (a). The client's
 * whole deposit and the invoice prepayment are withheld, the invoice is
 * cancelled and the deal moves to «Завершені», in one server transaction.
 * `confirm: true` is the dialog's explicit second step; the server refuses a
 * body without it.
 */
export async function forfeitDeposit(dealId: string, reason: string): Promise<DealForfeitResult> {
  const payload = await apiFetch<unknown>(`admin/deals/${dealId}/forfeit-deposit`, {
    method: 'POST',
    body: { reason: reason.trim(), confirm: true },
  });
  return DealForfeitResultSchema.parse(payload);
}

/**
 * The typed refusal code behind an `ApiError`, whichever envelope carried it.
 *
 * The routes this screen writes to answer in two shapes: the deals and relay
 * controllers put `code` at the top of the body, the finance handlers nest it
 * in `{ success: false, error: { code } }`. A guard-ladder 401/403 carries a
 * STRING on `error` and no `code`, so it reads as null — "not signed in" is
 * never mistaken for a business refusal — while the relay desk's own 403
 * `CLAIM_REQUIRED` (top-level `code`) is still read.
 */
export function dealActionErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.body;
  if (typeof body !== 'object' || body === null) return null;
  const top = (body as { code?: unknown }).code;
  if (typeof top === 'string') return top;
  const nested = (body as { error?: unknown }).error;
  if (typeof nested === 'object' && nested !== null) {
    const code = (nested as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return null;
}

/**
 * A field the refusal envelope carries beside its code (e.g. `auctionName` of
 * the ledger bridge's `UNKNOWN_LOCAL_AUCTION`), wherever the envelope put it.
 */
export function dealActionErrorDetail(error: unknown, field: string): string | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.body;
  if (typeof body !== 'object' || body === null) return null;
  const top = (body as Record<string, unknown>)[field];
  if (typeof top === 'string') return top;
  const nested = (body as { error?: unknown }).error;
  if (typeof nested === 'object' && nested !== null) {
    const value = (nested as Record<string, unknown>)[field];
    if (typeof value === 'string') return value;
  }
  return null;
}
